import {
    Component,
    Input,
    EventEmitter,
    Output,
    OnInit,
    ChangeDetectorRef,
    ChangeDetectionStrategy,
    ViewChild,
    ElementRef,
} from '@angular/core';
import { CanDisable, mixinDisabled, mixinInitialized, ThemePalette } from '@angular/material/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { AppService } from '@app/app.service';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';

/** Размер страницы по умолчанию, если нет размера страницы и не предусмотрены параметры размера страницы. */
export const DEFAULT_PAGE_SIZE = 200;

/**
 * Изменить объект события, который генерируется, когда пользователь выбирает
 * другой размер страницы или переход на другую страницу.
 */
export class PageEvent {
    /** Индекс текущей страницы. */
    pageIndex: number;

    /**
     * Индекс страницы, которая была выбрана ранее.
     */
    previousPageIndex?: number;

    /** Текущий размер страницы */
    pageSize: number;

    /** Текущее общее количество постраничных элементов */
    length: number;
}

// Базовый класс CustomPaginator.
export class CustomPaginatorBase {}

export const _MatPaginatorBase: any = mixinDisabled(mixinInitialized(CustomPaginatorBase));

@Component({
    selector: 'custom-paginator',
    exportAs: 'customPaginator',
    templateUrl: 'custom.pagination.component.html',
    styleUrls: ['custom.pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomPaginationComponent extends _MatPaginatorBase implements OnInit, CanDisable {
    private _initialized: boolean;

    @ViewChild('inputPageIndex') inputPageIndex: ElementRef;

    /** Заблокированность */
    @Input() disabled: boolean;

    /** Цвет темы, который будет использоваться для базовых элементов управления формы. */
    @Input() color: ThemePalette;

    /** Начинающийся с нуля индекс страницы отображаемого списка элементов. По умолчанию 0. */
    @Input()
    get pageIndex(): number {
        return this._pageIndex;
    }

    set pageIndex(value: number) {
        this._pageIndex = Math.max(coerceNumberProperty(value), 0);
        this._changeDetectorRef.markForCheck();
    }

    _pageIndex: number = 0;

    /** Длина общего количества элементов, которые разбиты на страницы. По умолчанию 0. */
    @Input()
    get length(): number {
        return this._length;
    }

    set length(value: number) {
        this._length = coerceNumberProperty(value);
        this._changeDetectorRef.markForCheck();
    }

    _length: number = 0;

    @Input()
    selectBottom: boolean = false;

    /** Количество элементов для отображения на странице. По умолчанию установлено значение 50. */
    @Input()
    get pageSize(): number {
        return this._pageSize;
    }

    set pageSize(value: number) {
        this._pageSize = Math.max(coerceNumberProperty(value), 0);
        this._updateDisplayedPageSizeOptions();
    }

    private _pageSize: number;

    /** Набор предоставленных параметров размера страницы для отображения пользователю. */
    @Input()
    get pageSizeOptions(): number[] {
        return this._pageSizeOptions;
    }

    set pageSizeOptions(value: number[]) {
        this._pageSizeOptions = (value || []).map((p) => coerceNumberProperty(p));
        this._updateDisplayedPageSizeOptions();
    }

    private _pageSizeOptions: number[] = [];

    /** Скрывать ли пользовательский интерфейс выбора размера страницы от пользователя. */
    @Input()
    get hidePageSize(): boolean {
        return this._hidePageSize;
    }

    set hidePageSize(value: boolean) {
        this._hidePageSize = coerceBooleanProperty(value);
    }

    private _hidePageSize = false;

    /** Показывать ли пользователю первый/последний пользовательский интерфейс кнопок. */
    @Input()
    get showFirstLastButtons(): boolean {
        return this._showFirstLastButtons;
    }

    set showFirstLastButtons(value: boolean) {
        this._showFirstLastButtons = coerceBooleanProperty(value);
    }

    private _showFirstLastButtons = false;

    /** Событие, генерируемое, когда paginator изменяет размер страницы или индекс страницы. */
    @Output() readonly page: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();

    /** Отображается набор параметров размера страницы. Будут отсортированы и включать текущий размер страницы. */
    _displayedPageSizeOptions: number[];

    @ViewChild('matSelect', { static: false }) matSelect: MatSelect;

    constructor(
        public appService: AppService,
        private _changeDetectorRef: ChangeDetectorRef,
    ) {
        super();
    }

    ngOnInit() {
        this._initialized = true;
        this._updateDisplayedPageSizeOptions();
        this._markInitialized();
        setTimeout(() => {
            this.matSelect._overlayPanelClass += 'custom-select-panel';
        });
    }

    /** Переход к следующей странице, если она существует. */
    nextPage(): void {
        if (!this.hasNextPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex++;
        this._emitPageEvent(previousPageIndex);
    }

    /** Перевод на предыдущую страницу, если она существует. */
    previousPage(): void {
        if (!this.hasPreviousPage()) {
            return;
        }

        const previousPageIndex = this.pageIndex;
        this.pageIndex--;
        this._emitPageEvent(previousPageIndex);
    }

    /** Есть ли предыдущая страница. */
    hasPreviousPage(): boolean {
        return this.pageIndex > 1 && this.pageSize != 0;
    }

    /** Есть ли следующая страница. */
    hasNextPage(): boolean {
        const maxPageIndex = this.getNumberOfPages();
        return this.pageIndex < maxPageIndex && this.pageSize != 0;
    }

    /** Подсчет количества страниц */
    getNumberOfPages(): number {
        if (!this.pageSize) {
            return 0;
        }

        return Math.ceil(this.length / this.pageSize);
    }

    /**
     * Изменяет размер страницы, так что первый элемент, отображаемый на странице, по-прежнему будет
     * отображается с использованием нового размера страницы.
     *
     * Например, если размер страницы 10 и на второй странице (элементы проиндексированы 10-19), то
     * переключение так, чтобы размер страницы был равен 5, установит третью страницу в качестве текущей
     * так что 10-й элемент все еще будет отображаться.
     */
    _changePageSize(pageSize: number) {
        this.pageIndex = 1;
        this.pageSize = pageSize;
        this._emitPageEvent(this.pageIndex);
    }

    /** Проверяет, должны ли быть отключены кнопки для перехода вперед. */
    _nextButtonsDisabled() {
        return this.disabled || !this.hasNextPage();
    }

    /** Проверяет, должны ли быть отключены кнопки для перехода назад. */
    _previousButtonsDisabled() {
        return this.disabled || !this.hasPreviousPage();
    }

    /**
     * Обновляет список параметров размера страницы для отображения пользователю.
     */
    private _updateDisplayedPageSizeOptions() {
        if (!this._initialized) {
            return;
        }

        // Если размер страницы не указан, используйте первый размер страницы или размер страницы по умолчанию.
        if (!this.pageSize) {
            this._pageSize = this.pageSizeOptions.length != 0 ? this.pageSizeOptions[0] : DEFAULT_PAGE_SIZE;
        }

        this._displayedPageSizeOptions = this.pageSizeOptions.slice();

        if (this._displayedPageSizeOptions.indexOf(this.pageSize) === -1) {
            this._displayedPageSizeOptions.push(this.pageSize);
        }

        // Сортировка чисел, использует функцию сортировки по номерам.
        this._displayedPageSizeOptions.sort((a, b) => a - b);
        this._changeDetectorRef.markForCheck();
    }

    /** Вызывает событие, уведомляющее о том, что изменение свойств пагинатора было инициировано. */
    private _emitPageEvent(previousPageIndex: number) {
        this.page.emit({
            previousPageIndex,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            length: this.length,
        });
    }

    /**
     * Получение нумерации страниц
     * @param page номер страницы
     * @param pageSize размер страницы
     * @param length длина массива записей
     * @returns
     */
    getRangeLabel(page, pageSize, length) {
        if (length === 0 || pageSize === 0) {
            return '0 ' + this.appService.language_dictionary.system_of + ' ' + length;
        }
        length = Math.max(length, 0);
        const startIndex = (page - 1) * pageSize;
        const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
        return startIndex + 1 + ' - ' + endIndex + ' ' + this.appService.language_dictionary.system_of + ' ' + length;
    }

    /**
     * Ввод страницы
     * @param value значение страницы
     * @param isKeyPress флаг нажатия кнопки
     */
    enterPageIndex(value, isKeyPress: boolean = false): string {
        // При отладке этой функции надо учитывать, что this.pageIndex
        // Это не обычная переменная, а вызов сеттера/геттера
        // А также то, что несмотря на привязку поля номера страницы к this.pageIndex
        // Фактически поле обновляется только при новом значении this.pageIndex

        // Часть проверок нельзя вынести в валидатор поля
        if (value == '' || value == this.pageIndex) {
            return;
        }

        if (value > this.getNumberOfPages()) {
            value = this.getNumberOfPages();
        }

        // Преобразовать в десятичное число
        value = parseInt(value, 10);
        value = isNaN(value) ? '1' : value;

        let normalValue = value - 1;

        if (isKeyPress) {
            normalValue = value;
        }

        // На данный момент this.pageIndex еще не равен введенному в поле
        const previousPageIndex = this.pageIndex;

        // Не увидел по коду как возможно !isKeyPress
        if (!isKeyPress && normalValue > 0 && value <= this.getNumberOfPages() && value != previousPageIndex + 1) {
            this.pageIndex = normalValue;
        }

        // После ввода номера страницы в поле
        if (isKeyPress && normalValue > 0 && normalValue <= this.getNumberOfPages()) {
            this.pageIndex = normalValue;
        } else {
            value = previousPageIndex + 1;
        }

        // На случай если значение поля не обновилось
        this.inputPageIndex.nativeElement.value = this.pageIndex;

        this._emitPageEvent(this.pageIndex);
        return value + '';
    }
}
